import { useState, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../contexts/AuthContext'
import VariantBuilder from '../components/VariantBuilder'
import StockTable from '../components/StockTable'
import './InventoryProducts.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}


const SALE_MODE_LABELS = {
    live: 'Vente directe',
    preorder: 'Précommande',
    made_to_order: 'Sur commande',
    drop: 'Drop',
    waitlist: 'Liste d\'attente',
}

const STATUS_LABELS = {
    draft: 'Brouillon',
    active: 'Actif',
    archived: 'Archivé',
}

const EMPTY_FORM = {
    name: '', description: '', category: '', price: '',
    saleMode: 'live',
    dropStartsAt: '', dropEndsAt: '',
    expectedDelivery: '', maxPreorders: '',
}

const InventoryProducts = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand
    const navigate = useNavigate()

    const [products, setProducts] = useState([])
    const [loading, setLoading] = useState(true)
    const [selected, setSelected] = useState(null) // product detail + stock
    const [detailLoading, setDetailLoading] = useState(false)

    // Modal création multi-étapes
    const [showModal, setShowModal] = useState(false)
    const [step, setStep] = useState(1) // 1: infos, 2: variants, 3: mode vente, 4: collections
    const [form, setForm] = useState(EMPTY_FORM)
    const [colors, setColors] = useState([])
    const [sizes, setSizes]   = useState([])
    const [collections, setCollections] = useState([])
    const [selectedCollIds, setSelectedCollIds] = useState([])
    const [creating, setCreating] = useState(false)
    const [createError, setCreateError] = useState(null)

    // Mode de vente rapide
    const [saleModeChanging, setSaleModeChanging] = useState(false)

    const loadProducts = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/products`)
            setProducts(data.products || [])
        } catch (err) {
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const loadCollections = async () => {
        if (!brandId) return
        try {
            const data = await apiFetch(`/brand/${brandId}/collections`)
            setCollections(data.collections || [])
        } catch { setCollections([]) }
    }

    const selectProduct = async (id) => {
        setDetailLoading(true)
        try {
            const [prodData, stockData] = await Promise.all([
                apiFetch(`/brand/${brandId}/products/${id}`),
                apiFetch(`/brand/${brandId}/products/${id}/stock`),
            ])
            setSelected({ product: prodData, stockRows: stockData })
        } catch (err) {
            console.error(err)
        } finally {
            setDetailLoading(false)
        }
    }

    useEffect(() => { loadProducts() }, [brandId])

    const openModal = () => {
        setForm(EMPTY_FORM)
        setColors([])
        setSizes([])
        setSelectedCollIds([])
        setCreateError(null)
        setStep(1)
        loadCollections()
        setShowModal(true)
    }

    const closeModal = () => setShowModal(false)

    const handleCreate = async () => {
        setCreating(true)
        setCreateError(null)
        try {
            const body = {
                name: form.name,
                description: form.description,
                category: form.category,
                price: Math.round(parseFloat(form.price) * 100), // centimes
                colors,
                sizes,
                saleMode: form.saleMode,
                collectionIds: selectedCollIds,
            }
            if (form.saleMode === 'drop' && form.dropStartsAt) {
                body.dropConfig = {
                    startsAt: form.dropStartsAt,
                    endsAt: form.dropEndsAt || null,
                }
            }
            if (form.saleMode === 'preorder' && form.expectedDelivery) {
                body.preorderConfig = {
                    expectedDelivery: form.expectedDelivery,
                    maxPreorders: form.maxPreorders ? parseInt(form.maxPreorders) : null,
                }
            }
            await apiFetch(`/brand/${brandId}/products`, {
                method: 'POST',
                body: JSON.stringify(body),
            })
            closeModal()
            await loadProducts()
        } catch (err) {
            setCreateError(err.message)
        } finally {
            setCreating(false)
        }
    }

    const handleChangeSaleMode = async (productId, saleMode) => {
        setSaleModeChanging(true)
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}/sale-mode`, {
                method: 'PATCH',
                body: JSON.stringify({ saleMode }),
            })
            setSelected(prev => ({ ...prev, product: { ...prev.product, saleMode } }))
            setProducts(prev => prev.map(p => p._id === productId ? { ...p, saleMode } : p))
        } catch (err) {
            alert(err.message)
        } finally {
            setSaleModeChanging(false)
        }
    }

    const handlePublish = async (productId) => {
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}`, {
                method: 'PATCH',
                body: JSON.stringify({ status: 'active' }),
            })
            setSelected(prev => ({ ...prev, product: { ...prev.product, status: 'active' } }))
            setProducts(prev => prev.map(p => p._id === productId ? { ...p, status: 'active' } : p))
        } catch (err) {
            alert(err.message)
        }
    }

    const handleArchive = async (productId) => {
        if (!window.confirm('Archiver ce produit ?')) return
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}`, { method: 'DELETE' })
            setSelected(null)
            setProducts(prev => prev.filter(p => p._id !== productId))
        } catch (err) {
            alert(err.message)
        }
    }

    const reloadStock = async () => {
        if (!selected) return
        try {
            const stockData = await apiFetch(`/brand/${brandId}/products/${selected.product._id}/stock`)
            setSelected(prev => ({ ...prev, stockRows: stockData }))
        } catch (err) {
            console.error(err)
        }
    }

    // ── VUE DÉTAIL ──────────────────────────────────────────────────────────

    if (detailLoading) return <div className="inv-loading">Chargement...</div>

    if (selected) {
        const { product, stockRows } = selected
        return (
            <div className="inv-detail">
                <div className="inv-detail-header">
                    <button className="inv-back-btn" onClick={() => setSelected(null)}>← Retour</button>
                    <h2 className="inv-detail-title">{product.name}</h2>
                    <div className="inv-detail-badges">
                        <span className={`inv-status-badge inv-status-badge--${product.status}`}>
                            {STATUS_LABELS[product.status]}
                        </span>
                        <span className="inv-mode-badge">{SALE_MODE_LABELS[product.saleMode]}</span>
                    </div>
                    {product.status === 'draft' && (
                        <button className="inv-btn-publish" onClick={() => handlePublish(product._id)}>
                            ↑ Publier
                        </button>
                    )}
                    <button className="inv-btn-danger-sm" onClick={() => handleArchive(product._id)}>
                        Archiver
                    </button>
                </div>

                <div className="inv-detail-meta">
                    <span><strong>Prix de base :</strong> {(product.price / 100).toFixed(2)} €</span>
                    {product.category && <span><strong>Catégorie :</strong> {product.category}</span>}
                    {product.description && <span>{product.description}</span>}
                </div>

                {/* Mode de vente */}
                <div className="inv-section">
                    <h3 className="inv-section-title">Mode de vente</h3>
                    <div className="inv-mode-select-row">
                        {Object.entries(SALE_MODE_LABELS).map(([mode, label]) => (
                            <button
                                key={mode}
                                className={`inv-mode-btn${product.saleMode === mode ? ' inv-mode-btn--active' : ''}`}
                                onClick={() => handleChangeSaleMode(product._id, mode)}
                                disabled={saleModeChanging || product.saleMode === mode}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Stock par variant */}
                <div className="inv-section">
                    <h3 className="inv-section-title">Stock par variant ({stockRows?.length ?? 0})</h3>
                    <StockTable
                        rows={stockRows}
                        brandId={brandId}
                        productId={product._id}
                        onUpdated={reloadStock}
                    />
                </div>

                {/* Drop config */}
                {product.dropConfig && (
                    <div className="inv-section">
                        <h3 className="inv-section-title">Configuration drop</h3>
                        <div className="inv-info-card">
                            {product.dropConfig.startsAt && (
                                <div className="inv-info-row">
                                    <span>Début</span>
                                    <span>{new Date(product.dropConfig.startsAt).toLocaleString('fr-FR')}</span>
                                </div>
                            )}
                            {product.dropConfig.endsAt && (
                                <div className="inv-info-row">
                                    <span>Fin</span>
                                    <span>{new Date(product.dropConfig.endsAt).toLocaleString('fr-FR')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Preorder config */}
                {product.preorderConfig && (
                    <div className="inv-section">
                        <h3 className="inv-section-title">Configuration précommande</h3>
                        <div className="inv-info-card">
                            {product.preorderConfig.expectedDelivery && (
                                <div className="inv-info-row">
                                    <span>Livraison estimée</span>
                                    <span>{new Date(product.preorderConfig.expectedDelivery).toLocaleDateString('fr-FR')}</span>
                                </div>
                            )}
                            {product.preorderConfig.maxPreorders && (
                                <div className="inv-info-row">
                                    <span>Max précommandes</span>
                                    <span>{product.preorderConfig.maxPreorders}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    // ── VUE LISTE ────────────────────────────────────────────────────────────

    if (loading) return <div className="inv-loading">Chargement...</div>

    return (
        <div className="inv-page">
            <div className="inv-header">
                <h2>Inventaire produits</h2>
                <button className="inv-btn-primary" onClick={openModal}>+ Nouveau produit</button>
            </div>

            {products.length === 0 ? (
                <p className="inv-empty">Aucun produit dans l'inventaire. Créez votre premier produit.</p>
            ) : (
                <div className="inv-list">
                    {products.map(p => (
                        <div key={p._id} className="inv-list-item" onClick={() => selectProduct(p._id)}>
                            <div className="inv-list-item-main">
                                <span className="inv-list-item-name">{p.name}</span>
                                <span className="inv-list-item-meta">
                                    {(p.price / 100).toFixed(2)} € · {p.variants?.length ?? 0} variant{p.variants?.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="inv-list-item-right">
                                <span className={`inv-status-badge inv-status-badge--${p.status}`}>{STATUS_LABELS[p.status]}</span>
                                <span className="inv-mode-badge inv-mode-badge--sm">{SALE_MODE_LABELS[p.saleMode]}</span>
                                <span className="inv-arrow">›</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Modal création ── */}
            {showModal && (
                <div className="inv-modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
                    <div className="inv-modal">
                        <div className="inv-modal-header">
                            <h3>Nouveau produit — étape {step}/4</h3>
                            <button className="inv-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <div className="inv-steps-bar">
                            {['Infos', 'Variants', 'Vente', 'Collections'].map((label, i) => (
                                <div key={i} className={`inv-step${step === i + 1 ? ' inv-step--active' : step > i + 1 ? ' inv-step--done' : ''}`}>
                                    <span className="inv-step-num">{step > i + 1 ? '✓' : i + 1}</span>
                                    <span className="inv-step-label">{label}</span>
                                </div>
                            ))}
                        </div>

                        {/* Étape 1 : infos générales */}
                        {step === 1 && (
                            <div className="inv-form">
                                <div className="inv-field">
                                    <label>Nom du produit *</label>
                                    <input
                                        value={form.name}
                                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                        placeholder="ex: Hoodie Classic"
                                    />
                                </div>
                                <div className="inv-field">
                                    <label>Catégorie</label>
                                    <input
                                        value={form.category}
                                        onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                        placeholder="Vêtements, Accessoires..."
                                    />
                                </div>
                                <div className="inv-field">
                                    <label>Prix de base (€) *</label>
                                    <input
                                        type="number" step="0.01" min="0"
                                        value={form.price}
                                        onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="inv-field">
                                    <label>Description</label>
                                    <textarea
                                        rows={3}
                                        value={form.description}
                                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                        placeholder="Description du produit"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Étape 2 : couleurs + tailles */}
                        {step === 2 && (
                            <div className="inv-form">
                                <p className="inv-hint">Ajoutez les couleurs et tailles disponibles pour ce produit.</p>
                                <VariantBuilder
                                    colors={colors}
                                    sizes={sizes}
                                    onChange={(newColors, newSizes) => { setColors(newColors); setSizes(newSizes) }}
                                />
                                {colors.length === 0 && sizes.length === 0 && (
                                    <p className="inv-hint-secondary">Sans couleur ni taille, le produit aura un seul variant.</p>
                                )}
                            </div>
                        )}

                        {/* Étape 3 : mode de vente */}
                        {step === 3 && (
                            <div className="inv-form">
                                <div className="inv-field">
                                    <label>Mode de vente</label>
                                    <div className="inv-mode-grid">
                                        {Object.entries(SALE_MODE_LABELS).map(([mode, label]) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                className={`inv-mode-btn${form.saleMode === mode ? ' inv-mode-btn--active' : ''}`}
                                                onClick={() => setForm(f => ({ ...f, saleMode: mode }))}
                                            >
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {form.saleMode === 'drop' && (
                                    <>
                                        <div className="inv-field">
                                            <label>Date d'ouverture du drop</label>
                                            <input
                                                type="datetime-local"
                                                value={form.dropStartsAt}
                                                onChange={e => setForm(f => ({ ...f, dropStartsAt: e.target.value }))}
                                            />
                                        </div>
                                        <div className="inv-field">
                                            <label>Date de fermeture (optionnel)</label>
                                            <input
                                                type="datetime-local"
                                                value={form.dropEndsAt}
                                                onChange={e => setForm(f => ({ ...f, dropEndsAt: e.target.value }))}
                                            />
                                        </div>
                                    </>
                                )}

                                {form.saleMode === 'preorder' && (
                                    <>
                                        <div className="inv-field">
                                            <label>Date de livraison estimée</label>
                                            <input
                                                type="date"
                                                value={form.expectedDelivery}
                                                onChange={e => setForm(f => ({ ...f, expectedDelivery: e.target.value }))}
                                            />
                                        </div>
                                        <div className="inv-field">
                                            <label>Nombre max de précommandes (optionnel)</label>
                                            <input
                                                type="number" min="1"
                                                value={form.maxPreorders}
                                                onChange={e => setForm(f => ({ ...f, maxPreorders: e.target.value }))}
                                                placeholder="Illimité si vide"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Étape 4 : collections */}
                        {step === 4 && (
                            <div className="inv-form">
                                <div className="inv-field">
                                    <label>Collections (optionnel)</label>
                                    {collections.length === 0 ? (
                                        <p className="inv-hint">Aucune collection disponible. Créez-en depuis la section Collections.</p>
                                    ) : (
                                        <div className="inv-coll-grid">
                                            {collections.map(c => (
                                                <button
                                                    key={c._id}
                                                    type="button"
                                                    className={`inv-coll-chip${selectedCollIds.includes(c._id) ? ' inv-coll-chip--sel' : ''}`}
                                                    onClick={() => setSelectedCollIds(prev =>
                                                        prev.includes(c._id) ? prev.filter(id => id !== c._id) : [...prev, c._id]
                                                    )}
                                                >
                                                    {c.name}
                                                    {selectedCollIds.includes(c._id) && ' ✓'}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {createError && <p className="inv-error">{createError}</p>}
                            </div>
                        )}

                        <div className="inv-modal-actions">
                            {step > 1 && (
                                <button className="inv-btn-outline" onClick={() => setStep(s => s - 1)} disabled={creating}>
                                    Retour
                                </button>
                            )}
                            {step < 4 ? (
                                <button
                                    className="inv-btn-primary"
                                    onClick={() => setStep(s => s + 1)}
                                    disabled={step === 1 && (!form.name || !form.price)}
                                >
                                    Suivant
                                </button>
                            ) : (
                                <button
                                    className="inv-btn-primary"
                                    onClick={handleCreate}
                                    disabled={creating}
                                >
                                    {creating ? 'Création...' : 'Créer le produit'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default InventoryProducts
